import React, { useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  Eye,
  FileText,
  Filter,
  Layers,
  List,
  RefreshCw,
  Terminal,
  X,
} from 'lucide-react';

const TASK_GROUPS = [
  {
    label: 'Batch Operations',
    icon: <Layers size={11} />,
    tasks: [
      {
        id: 'batch_approve',
        label: 'Approve All Pending',
        description: 'Move all pending submissions to approved status.',
        icon: <CheckCircle2 size={14} />,
        duration: 1200,
      },
      {
        id: 'batch_reject_stale',
        label: 'Flag Stale Submissions',
        description: 'Mark submissions pending longer than 30 days for review.',
        icon: <AlertTriangle size={14} />,
        duration: 1800,
      },
      {
        id: 'batch_normalize',
        label: 'Normalize Metadata',
        description: 'Title-case, trim whitespace, and fix encoding across submission fields.',
        icon: <Filter size={14} />,
        duration: 1800,
      },
    ],
  },
  {
    label: 'Queue Management',
    icon: <List size={11} />,
    tasks: [
      {
        id: 'queue_flush',
        label: 'Clear Stale Upload Jobs',
        description: 'Cancel batch upload jobs stuck in processing for more than 20 minutes.',
        icon: <Clock size={14} />,
        duration: 800,
      },
      {
        id: 'queue_retry',
        label: 'Retry Failed Uploads',
        description: 'Re-enqueue failed S3 upload jobs from the last 24 hours.',
        icon: <RefreshCw size={14} />,
        duration: 1500,
      },
      {
        id: 'queue_prioritize',
        label: 'Prioritize Pending Approvals',
        description: 'Move flagged content approval items to the top of the review queue.',
        icon: <AlertTriangle size={14} />,
        duration: 600,
      },
    ],
  },
  {
    label: 'Export & Reporting',
    icon: <FileText size={11} />,
    tasks: [
      {
        id: 'export_csv',
        label: 'Export Submissions CSV',
        description: 'Download the full submission list with metadata fields.',
        icon: <Database size={14} />,
        duration: 1000,
      },
      {
        id: 'export_report',
        label: 'Generate Status Report',
        description: 'Summary of pipeline health: pending, approved, rejected, and missing fields.',
        icon: <Eye size={14} />,
        duration: 1400,
      },
      {
        id: 'audit_s3',
        label: 'Audit S3 Staging Assets',
        description: 'Scan staging bucket for orphaned files not linked to active submissions.',
        icon: <Activity size={14} />,
        duration: 2200,
      },
    ],
  },
];

const AdvancedTasksPanel = ({ onClose }) => {
  const [runningTask, setRunningTask] = useState(null);
  const [completedTasks, setCompletedTasks] = useState(new Set());

  const simulateRun = (taskId, durationMs) => {
    setRunningTask(taskId);
    window.setTimeout(() => {
      setRunningTask(null);
      setCompletedTasks((prev) => new Set([...prev, taskId]));
    }, durationMs);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md admin-panel-fade-in" />
      <div
        className="relative w-full max-w-3xl max-h-[85vh] mx-4 bg-[#0a0a0a] border border-white/[0.1] rounded-xl shadow-2xl shadow-black/80 overflow-hidden admin-panel-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/[0.08] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#C4AB04]/10 flex items-center justify-center">
              <Terminal size={16} className="text-[#C4AB04]" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white tracking-tight">Advanced Tasks</h2>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">
                Local admin batch operations across the submission pipeline
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-white transition-colors"
            aria-label="Close advanced tasks"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)] space-y-6">
          {TASK_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2">
                {group.icon} {group.label}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {group.tasks.map((task) => {
                  const isRunning = runningTask === task.id;
                  const isDone = completedTasks.has(task.id);
                  return (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() => {
                        if (!isRunning) simulateRun(task.id, task.duration);
                      }}
                      disabled={isRunning}
                      className={`group text-left px-4 py-3 rounded-lg border transition-all duration-200 ${
                        isDone
                          ? 'border-[#39FF14]/20 bg-[#39FF14]/[0.03]'
                          : isRunning
                            ? 'border-[#C4AB04]/30 bg-[#C4AB04]/[0.04]'
                            : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.15] hover:bg-white/[0.04]'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`mt-0.5 shrink-0 transition-colors ${
                            isDone
                              ? 'text-[#39FF14]'
                              : isRunning
                                ? 'text-[#C4AB04] animate-pulse'
                                : 'text-gray-500 group-hover:text-white'
                          }`}
                        >
                          {isDone ? <CheckCircle2 size={14} /> : task.icon}
                        </div>
                        <div className="min-w-0">
                          <div
                            className={`text-[11px] font-bold tracking-wide ${
                              isDone ? 'text-[#39FF14]/80' : 'text-white'
                            }`}
                          >
                            {task.label}
                          </div>
                          <div className="text-[10px] text-gray-500 leading-relaxed mt-0.5">
                            {isRunning ? 'Running...' : task.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdvancedTasksPanel;
